/**
 * Step 8 · Social links cleanup.
 *
 * Three preview blocks with one-tap apply:
 *   1. Post/video links → profile URL      (counts toward Repaired)
 *   2. Suspect links   → Pending review    (counts toward Wrong when later rejected)
 *   3. Unfixable links → clear             (counts toward Wrong)
 *
 * All three preview lists are capped for page-speed; the button acts on
 * the full scope-wide set.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, Loader2, ArrowRight, ExternalLink, ShieldAlert, Wand2, Users,
  Trash2, Ban,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const PREVIEW_CAP = 300;

const FIELD_LABEL = {
  facebook_url: "Facebook", instagram_url: "Instagram", linkedin_url: "LinkedIn",
  twitter_url: "X / Twitter", tiktok_url: "TikTok", youtube_url: "YouTube",
  threads_url: "Threads",
};

const VIA_LABEL = {
  lastname_in_handle: "Last name in handle",
  fullname_in_handle: "Full name in handle",
  fullname_in_title: "Full name in title",
  site_query_plus_signal: "Site: query + signal",
  keyword_and_name: "Keyword + name",
  keyword_only: "Keyword only",
  brokerage_only: "Brokerage only",
  location_only: "Location only",
  firstname_only: "First-name partial",
  lastname_partial: "Last-name partial",
  possible_mismatch: "Possible mismatch",
  handle_mismatch: "Handle names nobody",
};

export default function SocialLinksSection({ onChange }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // "reduce" | "demote" | "unfixable"
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cleanup/social-links/preview");
      setPreview(data);
    } catch (e) {
      toast.error("Preview failed", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = preview?.by_action || {};
  const reduceItems = preview?.reduce || [];
  const cosmeticItems = preview?.normalize || [];
  const suspectItems = preview?.suspect || [];
  const unfixableItems = preview?.unfixable || [];

  // Reduce + cosmetic are applied together as a single "rewrite to profile URLs"
  const rewritable = useMemo(
    () => [...reduceItems, ...cosmeticItems],
    [reduceItems, cosmeticItems],
  );

  async function applyRewrite() {
    setBusy(true);
    try {
      const { data } = await api.post("/cleanup/social-links/apply", {
        actions: ["reduce", "normalize"],
      });
      toast.success(`Rewrote ${data.changed} links across ${data.realtors} realtors`);
      setConfirm(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Rewrite failed", { description: formatApiError(e) });
    } finally { setBusy(false); }
  }

  async function applyDemote() {
    setBusy(true);
    try {
      const { data } = await api.post("/cleanup/social-links/demote", {});
      toast.success(`Moved ${data.demoted} suspect links to Pending review across ${data.realtors} realtors`);
      setConfirm(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Demote failed", { description: formatApiError(e) });
    } finally { setBusy(false); }
  }

  async function applyUnfixable() {
    setBusy(true);
    try {
      const { data } = await api.post("/cleanup/social-links/clear", {
        reason: "unfixable_bulk",
      });
      toast.success(`Cleared ${data.cleared} unfixable links across ${data.realtors} realtors`);
      setConfirm(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Clear failed", { description: formatApiError(e) });
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="social-links-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-slate-600">
          Scoped to your Locations. Every apply logs to the Cleanup audit trail
          and updates the Admin dashboard's <strong>Wrong / Repaired</strong> columns.
        </p>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          disabled={loading}
          className="shrink-0 min-h-[44px]"
          data-testid="social-links-refresh"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh preview
        </Button>
      </div>

      {!preview ? (
        <div className="text-sm text-slate-500 italic flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning stored social links…
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="social-links-counts">
          <CountTile label="Post → profile" value={counts.reduce || 0} tone="sky" />
          <CountTile label="Cosmetic fixes" value={counts.normalize || 0} tone="slate" />
          <CountTile label="Suspect" value={counts.suspect || 0} tone="amber" />
          <CountTile label="Unfixable" value={counts.unfixable || 0} tone="rose" />
        </div>
      )}

      {/* --------------- Block 1: Post/video → profile --------------- */}
      <Block
        icon={Wand2}
        tone="sky"
        title="Post / video links → profile URL"
        subtitle={
          rewritable.length === 0
            ? "Nothing to rewrite — every stored link already points at a profile."
            : `${reduceItems.length} post/video link${reduceItems.length === 1 ? "" : "s"} + ${cosmeticItems.length} cosmetic fix${cosmeticItems.length === 1 ? "" : "es"} (http→https, twitter.com→x.com, tracking query strings).`
        }
        preview={rewritable.slice(0, PREVIEW_CAP)}
        renderRow={(it) => (
          <RewriteRow item={it} key={`${it.realtor_id}-${it.field}`} />
        )}
        action={
          rewritable.length > 0 ? (
            <Button
              className="bg-sky-600 hover:bg-sky-700"
              onClick={() => setConfirm("reduce")}
              disabled={busy}
              data-testid="social-links-rewrite-button"
            >
              <Wand2 className="h-4 w-4 mr-1" />
              Rewrite {rewritable.length} link{rewritable.length === 1 ? "" : "s"} to profile URLs
            </Button>
          ) : null
        }
      />

      {/* --------------- Block 2: Suspect → pending review --------------- */}
      <Block
        icon={ShieldAlert}
        tone="amber"
        title="Suspect links → move to Pending review"
        subtitle={
          suspectItems.length === 0
            ? "No suspect links — every stored handle contains part of the realtor's name."
            : `${suspectItems.length} stored link${suspectItems.length === 1 ? "" : "s"} whose handle names nobody in the realtor's name. Each one moves to that realtor's Pending review queue so a person can accept or reject it.`
        }
        preview={suspectItems.slice(0, PREVIEW_CAP)}
        renderRow={(it) => <SuspectRow item={it} key={`${it.realtor_id}-${it.field}`} />}
        action={
          suspectItems.length > 0 ? (
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setConfirm("demote")}
              disabled={busy}
              data-testid="social-links-demote-button"
            >
              <ShieldAlert className="h-4 w-4 mr-1" />
              Move {suspectItems.length} suspect link{suspectItems.length === 1 ? "" : "s"} to review
            </Button>
          ) : null
        }
      />

      {/* --------------- Block 3: Unfixable → clear --------------- */}
      <Block
        icon={Ban}
        tone="rose"
        title="Unfixable links → clear"
        subtitle={
          unfixableItems.length === 0
            ? "No unfixable links stored."
            : `${unfixableItems.length} link${unfixableItems.length === 1 ? "" : "s"} that can never resolve to a profile — short links (vm.tiktok.com/…, fb.watch/…, youtu.be/…), posts without a handle, wrong host. Clearing them counts toward Wrong on the Admin dashboard.`
        }
        preview={unfixableItems.slice(0, PREVIEW_CAP)}
        renderRow={(it) => <UnfixableRow item={it} key={`${it.realtor_id}-${it.field}`} />}
        action={
          unfixableItems.length > 0 ? (
            <Button
              variant="destructive"
              onClick={() => setConfirm("unfixable")}
              disabled={busy}
              data-testid="social-links-clear-button"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear {unfixableItems.length} unfixable link{unfixableItems.length === 1 ? "" : "s"}
            </Button>
          ) : null
        }
      />

      <AlertDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o && !busy) setConfirm(null); }}
      >
        <AlertDialogContent data-testid="social-links-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "reduce"
                ? `Rewrite ${rewritable.length} link${rewritable.length === 1 ? "" : "s"} to profile URLs?`
                : confirm === "demote"
                ? `Move ${suspectItems.length} suspect link${suspectItems.length === 1 ? "" : "s"} to review?`
                : `Clear ${unfixableItems.length} unfixable link${unfixableItems.length === 1 ? "" : "s"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "reduce" ? (
                <>Every post / video / sub-page URL whose handle IS the realtor's own will be
                shortened to that handle's profile page. Cosmetic fixes (http→https,
                twitter.com→x.com, tracking query strings, trailing slash) are applied at the
                same time. Verified flags carry over. Counts toward <strong>Repaired</strong>.</>
              ) : confirm === "demote" ? (
                <>Each suspect link is removed from the realtor's stored field and appears in
                that realtor's Pending review queue. If a reviewer later rejects it, it counts
                toward <strong>Wrong</strong>. Accepting a candidate simply restores it into
                the field.</>
              ) : (
                <>These links can never resolve to a profile. Each one is cleared from the
                realtor and logged in <code>social_review_log</code>. Counts toward{" "}
                <strong>Wrong</strong> on the Admin dashboard.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="social-links-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={
                confirm === "reduce" ? applyRewrite
                : confirm === "demote" ? applyDemote
                : applyUnfixable
              }
              className={
                confirm === "reduce" ? "bg-sky-600 hover:bg-sky-700 focus:ring-sky-600"
                : confirm === "demote" ? "bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
                : "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              }
              data-testid="social-links-confirm-apply"
            >
              {busy ? "Applying…" : "Apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CountTile({ label, value, tone }) {
  const cls = {
    sky: "bg-sky-50 text-sky-800 border-sky-100",
    slate: "bg-slate-50 text-slate-800 border-slate-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    rose: "bg-rose-50 text-rose-800 border-rose-100",
  }[tone];
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-medium opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function Block({ icon: Icon, tone, title, subtitle, preview, renderRow, action }) {
  const [expanded, setExpanded] = useState(false);
  const empty = !preview || preview.length === 0;
  const dot = {
    sky: "bg-sky-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
  }[tone];
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
      <div className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`mt-0.5 h-2.5 w-2.5 rounded-full ${dot} shrink-0`} />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 flex items-center gap-2">
              <Icon className="h-4 w-4 text-slate-500" /> {title}
            </div>
            <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!empty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`social-links-block-expand-${tone}`}
            >
              {expanded ? "Hide preview" : `Preview (${preview.length}${preview.length >= PREVIEW_CAP ? "+" : ""})`}
            </Button>
          )}
          {action}
        </div>
      </div>
      {expanded && !empty && (
        <div className="max-h-96 overflow-y-auto border-t border-slate-100 bg-slate-50/50">
          {preview.map((it) => renderRow(it))}
        </div>
      )}
    </div>
  );
}

function RowBase({ item, children, extraBadge }) {
  return (
    <div className="flex flex-col md:flex-row md:items-start gap-2 px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-white/70 text-sm">
      <div className="md:w-56 shrink-0">
        <Link
          to={`/realtors/${item.realtor_id}`}
          className="font-medium text-slate-900 hover:underline"
          data-testid={`social-row-realtor-${item.realtor_id}-${item.field}`}
        >
          {item.realtor_name || "—"}
        </Link>
        {item.brokerage_name ? (
          <div className="text-xs text-slate-500 truncate">{item.brokerage_name}</div>
        ) : null}
      </div>
      <div className="md:w-24 shrink-0">
        <Badge variant="outline" className="text-[11px]">{FIELD_LABEL[item.field] || item.field}</Badge>
      </div>
      <div className="flex-1 min-w-0">{children}</div>
      {extraBadge}
    </div>
  );
}

function RewriteRow({ item }) {
  return (
    <RowBase item={item}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-slate-500 line-through decoration-slate-400 break-all hover:text-slate-700"
        >
          {item.url}
        </a>
        <ArrowRight className="h-3 w-3 text-sky-500 shrink-0" />
        <a
          href={item.target}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-sky-700 break-all hover:underline"
        >
          {item.target}
        </a>
        {item.detail ? (
          <span className="text-[10px] uppercase tracking-wider text-slate-400">{item.detail}</span>
        ) : null}
        {item.verified ? (
          <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">verified — flag carries</Badge>
        ) : null}
      </div>
    </RowBase>
  );
}

function SuspectRow({ item }) {
  return (
    <RowBase
      item={item}
      extraBadge={
        item.shared > 1 ? (
          <Badge
            className="bg-amber-100 text-amber-900 border-amber-200 gap-1 shrink-0"
            variant="outline"
            data-testid={`social-row-shared-${item.realtor_id}-${item.field}`}
          >
            <Users className="h-3 w-3" /> shared by {item.shared}
          </Badge>
        ) : null
      }
    >
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-slate-700 break-all hover:underline inline-flex items-center gap-1"
        >
          {item.url} <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        {item.handle ? (
          <Badge variant="outline" className="text-[10px]">
            handle: <span className="font-mono ml-1">{item.handle}</span>
          </Badge>
        ) : null}
        {item.matched_via ? (
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            found via {VIA_LABEL[item.matched_via] || item.matched_via}
          </span>
        ) : null}
        {item.brokerage_page ? (
          <Badge variant="outline" className="text-[10px] bg-slate-50">brokerage handle</Badge>
        ) : null}
      </div>
    </RowBase>
  );
}

function UnfixableRow({ item }) {
  return (
    <RowBase item={item}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-slate-700 break-all hover:underline inline-flex items-center gap-1"
        >
          {item.url} <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
        {item.detail ? (
          <span className="text-[10px] uppercase tracking-wider text-slate-500">{item.detail}</span>
        ) : null}
      </div>
    </RowBase>
  );
}
